import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Box, Typography } from '@mui/material';

import { useModal } from 'mui-modal-provider';

import dayjs from 'dayjs';
import { capitalize } from 'lodash';

import LazyAvatar from '../../components/Avatar/LazyAvatar/LazyAvatar';
import ListFavouritesUsersDialog from '../../components/Dialogs/ListFavouritesUsers/ListFavouritesUsersDialog';
import { TitleComponent } from '../../components/Dialogs/ListFavouritesUsers/TitleComponent';
import { SubscriptionPlan, mapPlanLabel } from '../../components/Layout/Header/Header';
import { OnboardingStepper } from '../../components/OnboardingStepper/OnboardingStepper';
import { PromotionUpsellCard } from '../../components/PromotionUpsellBanner/PromotionUpsellCard';
import { TikTokOfferBanner } from '../../components/TikTokOfferBanner/TikTokOfferBanner';
import { enableShopifyVenueID } from '../../config';
import { useReservationLimitInPeriodContext } from '../../contexts/ReservationsLimitInPeriodContext';
import { UserAccessRequestResultValue, Venue } from '../../graphql/API';
import useAccessRequestQuery from '../../hooks/reactQuery/useAccessRequestQuery';
import useListReservationContentQuery from '../../hooks/reactQuery/useListReservationContentQuery';
import { useListOfferQuery } from '../../hooks/reactQuery/useOfferQuery';
import useVenueGetReservationContentCountQuery from '../../hooks/reactQuery/useVenueGetReservationContentCountQuery';
import { useVenueListAccessRequestsCountQuery } from '../../hooks/reactQuery/accessRequest/useVenueListAccessRequestsCountQuery';
import { useVenueListReservationsCountQuery } from '../../hooks/reactQuery/reservations/useVenueListReservationsCountQuery';
import { useAppSelector } from '../../redux/hooks';
import { getBrandPromotionAllowed } from '../../utils/reservations';
import { VENUE_MANUAL_TAGS } from '../../utils/venue-tags';
import { extractSubscriptionPlan } from '../SubscriptionPlans/common/helper';
import DashboardCard from './components/DashboardCard';
import SkeletonLoading from './components/SkeletonLoading';

type StatCard = { label: string; key: string };

const Dashboard: React.FC = () => {
  const venueState = useAppSelector((state) => state.venue.value);
  const { subscription } = useAppSelector((state) => state.subscription.value);

  const { periodEndDate } = useReservationLimitInPeriodContext() || {};

  const isOnlineBrand = venueState?.tags?.includes(VENUE_MANUAL_TAGS.Online) || false;
  const isBusinessVenue = venueState?.tags?.includes(VENUE_MANUAL_TAGS.VenuePlans) || false;
  const isFreeUser = !subscription?.sku;

  const isShopifyVenue =
    !!enableShopifyVenueID && String(venueState?.id) === String(enableShopifyVenueID);

  const cards: StatCard[] = React.useMemo(
    () =>
      isOnlineBrand
        ? [
            { label: 'Product requests', key: 'accessRequestsCount' },
            { label: 'Content received', key: 'contentReceived' },
            { label: 'Favorites', key: 'totalFavouritesByActiveUsers' },
          ]
        : [
            { label: 'Content received', key: 'contentReceived' },
            { label: 'Favorites', key: 'totalFavouritesByActiveUsers' },
            { label: 'Total reservations', key: 'totalReservations' },
          ],
    [isOnlineBrand],
  );

  const { showModal } = useModal();
  const navigate = useNavigate();

  const { data: accessRequestsCountData } = useVenueListAccessRequestsCountQuery(
    {
      status: UserAccessRequestResultValue.requested,
    },
    isOnlineBrand,
  );
  const accessRequestsCount = Number(accessRequestsCountData?.totalAll ?? 0);

  const { data: accessRequests } = useAccessRequestQuery(
    {
      sortBy: 'createdAt',
      sortDirection: 'desc',
      status: UserAccessRequestResultValue.requested,
      marketRegions: [],
    },
    !isOnlineBrand || accessRequestsCount <= 0,
    9,
  );

  const productRequestAvatars = React.useMemo(
    () =>
      accessRequests.slice(0, 9).map((req) => ({
        src: req?.user?.picture?.medium ?? undefined,
        placeholderSrc: req?.user?.picture?.thumbnail ?? undefined,
        alt: `${req?.user?.firstname ?? ''} ${req?.user?.lastname ?? ''}`.trim(),
      })),
    [accessRequests],
  );

  // Brands prefer Instagram/TikTok posts; venues accept any received content.
  const receivedContentFilter = React.useMemo(
    () => (isOnlineBrand ? { byVenue: false, isPost: true } : { byVenue: false }),
    [isOnlineBrand],
  );

  const { data: receivedPostsData } = useListReservationContentQuery(
    receivedContentFilter,
    9,
    true,
  );

  const receivedPosts = React.useMemo(
    () =>
      (receivedPostsData ?? []).slice(0, 9).map((item) => ({
        src: item.images?.medium ?? item.images?.small ?? item.images?.thumbnail ?? undefined,
        placeholderSrc: item.images?.thumbnail ?? undefined,
        videoSrc: item.images?.video ?? undefined,
      })),
    [receivedPostsData],
  );

  const { data: receivedContentCountData } = useVenueGetReservationContentCountQuery(
    receivedContentFilter,
  );

  const receivedContentCount = React.useMemo(
    () =>
      (receivedContentCountData?.totalPostOnInstagramStories ?? 0) +
      (receivedContentCountData?.totalPostOnInstagramFeed ?? 0) +
      (receivedContentCountData?.totalPostOnInstagramReels ?? 0) +
      (receivedContentCountData?.totalPostOnTikTokVideo ?? 0),
    [receivedContentCountData],
  );

  const daysUntilReset = React.useMemo(() => {
    if (!periodEndDate) return undefined;
    const diff = dayjs(periodEndDate).startOf('day').diff(dayjs().startOf('day'), 'day');
    return Math.max(diff, 0);
  }, [periodEndDate]);

  const handleListFavouritesDialog = React.useCallback(() => {
    showModal(ListFavouritesUsersDialog, {
      titleComponent: (
        <TitleComponent
          count={venueState ? Number(venueState['totalFavouritesByActiveUsers']) : 0}
        />
      ),
    });
  }, [showModal, venueState]);

  const subscriptionPlan = React.useMemo(() => {
    const [plan] = extractSubscriptionPlan(subscription?.sku ?? '');

    return plan;
  }, [subscription]);

  const { data: brandOffers } = useListOfferQuery(undefined, 1);

  // Live counters (refetched on dashboard mount, independent of the venue redux state
  // which can lag behind shipment confirmations).
  const { data: brandReservationsCount } = useVenueListReservationsCountQuery(
    { venueID: venueState?.id || '' },
    isOnlineBrand && !!venueState?.id,
  );

  const onboardingStepper = React.useMemo(() => {
    if (!isOnlineBrand) return null;

    // Step 3 is "done" the moment a reservation reaches any post-waiting shipping
    // state: shipped → in transit → delivered → checked-in (the backend uses
    // totalCheckedIn as the canonical "delivered" counter for product venues; the
    // legacy totalDeliveredReservations always returns 0).
    const totalShippedReservations =
      Number(brandReservationsCount?.totalShipped ?? 0) +
      Number(brandReservationsCount?.totalShippedWithoutInvite ?? 0) +
      Number(brandReservationsCount?.totalInTransitReservations ?? 0) +
      Number(brandReservationsCount?.totalDeliveredReservations ?? 0) +
      Number(brandReservationsCount?.totalCheckedIn ?? 0);
    const totalAnyReservations =
      Number(brandReservationsCount?.totalAll ?? 0) ||
      Number(venueState?.totalReservations ?? 0);

    const step1Done = (brandOffers?.length ?? 0) > 0;
    const step2Done = totalAnyReservations > 0;
    const step3Done = totalShippedReservations > 0;
    const allDone = step1Done && step2Done && step3Done;

    return {
      allDone,
      steps: [
        {
          done: step1Done,
          title: 'Create your first product',
          description:
            'List your products and define the promotion you want in return.',
          cta: 'Create',
          to: '/products/create',
        },
        {
          done: step2Done,
          title: 'Accept a product request',
          description:
            'Review incoming requests and approve users to promote your brand.',
          cta: 'Requests',
          to: '/access-requests',
        },
        {
          done: step3Done,
          title: 'Ship the product',
          description:
            'Fulfill the order, and you will receive the promotion within 7 days.',
          cta: 'Reservations',
          to: '/reservations',
        },
      ],
    };
  }, [
    isOnlineBrand,
    brandOffers,
    brandReservationsCount,
    venueState?.totalReservations,
  ]);

  const stepperKey = `bp-onboarding-stepper-done-${venueState?.id ?? 'default'}`;
  const [stepperHidden, setStepperHidden] = React.useState<boolean>(() => {
    try {
      return localStorage.getItem(stepperKey) === '1';
    } catch {
      return false;
    }
  });
  React.useEffect(() => {
    try {
      setStepperHidden(localStorage.getItem(stepperKey) === '1');
    } catch {
      setStepperHidden(false);
    }
  }, [stepperKey]);
  React.useEffect(() => {
    if (onboardingStepper?.allDone && !stepperHidden) {
      try {
        localStorage.setItem(stepperKey, '1');
      } catch {
        /* ignore */
      }
      setStepperHidden(true);
    }
  }, [onboardingStepper?.allDone, stepperHidden, stepperKey]);

  const showOnboardingStepper =
    !!onboardingStepper && !onboardingStepper.allDone && !stepperHidden;

  const sp = React.useMemo(() => mapPlanLabel[subscriptionPlan], [subscriptionPlan]);

  const promotionsCard = React.useMemo(() => {
    const consumed = venueState?.totalConsumedPromotions ?? 0;
    // For brands prefer the SKU-derived limit (backend totalAllowedPromotions can lag
    // behind v3 plan changes — e.g. new Basic = 3, legacy Basic = 5). Venues still
    // trust the backend value.
    const backendAllowed = venueState?.totalAllowedPromotions ?? 0;
    const brandAllowed = isOnlineBrand
      ? getBrandPromotionAllowed(subscription?.sku, subscriptionPlan)
      : 0;
    const allowed = isOnlineBrand && subscription?.sku ? brandAllowed : backendAllowed;
    const isPlus = subscriptionPlan === 'plus';
    const hasPlan = isBusinessVenue || !!subscription?.sku;
    const limitReached = !!allowed && consumed >= allowed && !isPlus;
    const planLabel = isPlus
      ? 'PLUS'
      : subscription?.sku
        ? capitalize(subscriptionPlan)
        : 'FREE';
    const resetDate = dayjs(periodEndDate).format('MMMM D, YYYY');

    const planLink = (
      <b>
        <Link style={{ color: '#1A1C1E' }} to='/plans'>
          {planLabel} Plan
        </Link>
      </b>
    );

    const value = hasPlan
      ? `${consumed}${!isPlus && allowed ? `/${allowed}` : ''}`
      : '0';

    let subText: React.ReactNode;
    if (!hasPlan) {
      subText = (
        <>
          <Link style={{ color: '#1A1C1E', fontWeight: 600 }} to='/plans'>
            Upgrade to a plan
          </Link>{' '}
          to start receiving promotions.
        </>
      );
    } else if (isPlus) {
      subText = (
        <>
          Unlimited promotions included in your {planLink}.
          <br />
          Resets on {resetDate}.
        </>
      );
    } else if (limitReached) {
      subText = (
        <>
          You&rsquo;ve reached your monthly promotions limit.
          <br />
          Resets on {resetDate}.
        </>
      );
    } else {
      subText = (
        <>
          Included in your {planLink}.
          <br />
          Resets on {resetDate}.
        </>
      );
    }

    const dark = limitReached || !hasPlan;
    const cta = dark
      ? { label: hasPlan ? 'Receive more promotions' : 'Get started', to: '/plans' }
      : undefined;
    const upsellTarget: 'starter' | 'plus' | undefined = !isPlus
      ? subscriptionPlan === 'starter'
        ? 'plus'
        : 'starter'
      : undefined;

    return { value, subText, dark, cta, limitReached, isPlus, upsellTarget };
  }, [
    venueState,
    subscriptionPlan,
    isBusinessVenue,
    isOnlineBrand,
    subscription?.sku,
    periodEndDate,
  ]);

  return venueState ? (
    <Box height='100%' display='flex' flexDirection='column' sx={{ backgroundColor: '#ffffff' }}>
      <Box sx={{ pt: 4, px: 4, pb: 2 }}>
        <Box display='flex' alignItems='center' gap={2} flexWrap='wrap' sx={{ mb: 3 }}>
          <LazyAvatar
            variant='circular'
            placeholderSrc={String(venueState?.picture?.thumbnail)}
            src={String(venueState?.picture?.medium)}
            sx={{ width: 40, height: 40 }}
          />

          <Typography variant='h6' fontWeight='bold'>
            Hi {venueState?.name}
          </Typography>

          {!isShopifyVenue &&
            (isFreeUser ? (
              <Typography component='div' variant='body2'>
                <Box component='span' sx={{ fontWeight: 500, mr: 0.5 }}>
                  FREE
                </Box>
                Plan
              </Typography>
            ) : (
              sp && (
                <Box
                  sx={{
                    position: 'relative',
                    display: 'inline-flex',
                    isolation: 'isolate',
                    '& .MuiChip-root': { m: 0 },
                  }}
                >
                  {subscriptionPlan !== 'basic' && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: '-1px',
                        zIndex: 0,
                        borderRadius: '9px',
                        overflow: 'hidden',
                        pointerEvents: 'none',
                        filter: 'blur(0.6px)',
                        opacity: 0.85,
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          width: 220,
                          height: 220,
                          transform: 'translate(-50%, -50%)',
                          background:
                            subscriptionPlan === 'plus'
                              ? 'conic-gradient(from 0deg, transparent 0%, transparent 76%, #E0A3FF 84%, #FF6FD8 92%, transparent 100%)'
                              : 'conic-gradient(from 0deg, transparent 0%, transparent 76%, #9BE86B 84%, #2FA600 92%, transparent 100%)',
                          animation: 'planBadgeGlowSpin 5s linear infinite',
                          '@keyframes planBadgeGlowSpin': {
                            to: { transform: 'translate(-50%, -50%) rotate(360deg)' },
                          },
                        }}
                      />
                    </Box>
                  )}

                  <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <SubscriptionPlan
                      size='small'
                      label={sp.label}
                      planColor={sp.color}
                      fill={sp.fill}
                      bgColor={sp.bgColor}
                    />
                  </Box>
                </Box>
              )
            ))}
        </Box>

        <Box display='flex' gap={2} alignItems='stretch' flexWrap='wrap'>
          {!isShopifyVenue && (
            <DashboardCard
              label='Monthly promotions'
              hideLabel={promotionsCard.limitReached}
              subTextEmphasized={promotionsCard.limitReached}
              value={promotionsCard.value}
              subText={promotionsCard.subText}
              dark={promotionsCard.dark}
              cta={promotionsCard.cta}
              onClick={() => navigate('/reservations?monthly=1')}
            />
          )}

          {cards.map((x) => {
            const isProductRequests = x.key === 'accessRequestsCount';
            const isFavoritesCard = x.key === 'totalFavouritesByActiveUsers';
            const isContentReceived = x.key === 'contentReceived';
            const favCount = isContentReceived
              ? 0
              : Number(venueState[x.key as keyof Venue]);
            const cardCount = isProductRequests
              ? accessRequestsCount
              : isContentReceived
                ? receivedContentCount
                : favCount;
            const isPlus = subscriptionPlan === 'plus';

            let cardSubText: React.ReactNode;
            if (isOnlineBrand) {
              if (isProductRequests && productRequestAvatars.length === 0) {
                cardSubText = 'Models who want to promote your brand';
              } else if (isContentReceived && receivedPosts.length === 0) {
                cardSubText = 'Posts models shared about you';
              } else if (isFavoritesCard) {
                cardSubText = 'Models who saved your brand';
              }
            }

            return (
              <DashboardCard
                key={x.key}
                label={x.label}
                count={cardCount}
                subText={cardSubText}
                subscriptionPlan={subscriptionPlan}
                avatars={isProductRequests ? productRequestAvatars : undefined}
                avatarsExtraCount={
                  isProductRequests ? Math.max(accessRequestsCount - 9, 0) : 0
                }
                contentThumbs={isContentReceived ? receivedPosts : undefined}
                onClick={
                  x.key === 'totalReservations'
                    ? () => navigate('/reservations')
                    : isProductRequests
                      ? () => navigate('/access-requests')
                      : isContentReceived
                        ? () => navigate('/content')
                        : isFavoritesCard && favCount > 0
                          ? isPlus
                            ? handleListFavouritesDialog
                            : () => navigate('/plans')
                          : undefined
                }
              />
            );
          })}
        </Box>
      </Box>

      {!isShopifyVenue && (
        <Box
          sx={{
            px: 4,
            pb: 4,
            pt: 0.5,
            display: 'flex',
            gap: 2,
            alignItems: 'stretch',
            flexWrap: 'wrap',
          }}
        >
          {showOnboardingStepper && onboardingStepper ? (
            <Box
              sx={{
                flex: '1 1 360px',
                minWidth: 320,
                maxWidth: 720,
                display: 'flex',
              }}
            >
              <OnboardingStepper steps={onboardingStepper.steps} />
            </Box>
          ) : (
            <>
              {promotionsCard.limitReached && promotionsCard.upsellTarget && (
                <PromotionUpsellCard
                  target={promotionsCard.upsellTarget}
                  kind={isOnlineBrand ? 'brand' : 'venue'}
                  pendingCount={isOnlineBrand ? accessRequestsCount : 0}
                  avatars={isOnlineBrand ? productRequestAvatars : []}
                  posts={receivedPosts}
                  daysUntilReset={daysUntilReset}
                />
              )}
              <TikTokOfferBanner />
            </>
          )}
        </Box>
      )}
    </Box>
  ) : (
    <SkeletonLoading />
  );
};

export default Dashboard;
